/**
 * NewChatDrawer — painel lateral para iniciar uma nova conversa.
 *
 * Permite buscar e selecionar:
 *  - Uma pessoa (abre DM direto)
 *  - Uma Organização (abre o grupo da Org ou lista membros individualmente)
 */
import { useState } from 'react'
import {
  Drawer,
  TextInput,
  Stack,
  Text,
  Group,
  Avatar,
  UnstyledButton,
  Accordion,
  Badge,
  Loader,
  ScrollArea,
  Box,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconSearch } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import {
  useChatPeople,
  useChatOrganizations,
  useOpenDm,
  useOpenOrg,
} from '@/hooks/useChatDirectory'

function getInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

interface Props {
  opened: boolean
  onClose: () => void
}

export function NewChatDrawer({ opened, onClose }: Props) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')

  const people = useChatPeople()
  const orgs = useChatOrganizations()
  const openDm = useOpenDm()
  const openOrg = useOpenOrg()

  const norm = (s: string) => s.toLowerCase()
  const ql = norm(q.trim())

  const filteredPeople = (people.data ?? []).filter(
    (p) => !ql || norm(p.name || '').includes(ql) || norm(p.email || '').includes(ql),
  )
  const filteredOrgs = (orgs.data ?? []).filter(
    (o) => !ql || norm(o.name).includes(ql),
  )

  const go = (cid: number) => {
    onClose()
    navigate(`/chat/${cid}`)
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={360}
      title={
        <Text fw={600} size="sm">
          Nova conversa
        </Text>
      }
      styles={{
        header: {
          borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
          paddingBottom: 'var(--mantine-spacing-sm)',
        },
      }}
    >
      <TextInput
        placeholder="Buscar pessoa ou organização…"
        leftSection={<IconSearch size={16} />}
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
        mb="md"
        aria-label="Buscar"
      />

      <ScrollArea style={{ height: 'calc(100vh - 160px)' }}>
        <Stack gap="lg">
          {/* Seção: Pessoas */}
          <Box>
            <Text fw={600} size="sm" c="dimmed" mb="xs">
              Pessoas
            </Text>
            {people.isLoading && (
              <Group justify="center" py="sm">
                <Loader size="sm" type="dots" color="teal" />
              </Group>
            )}
            {!people.isLoading && filteredPeople.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="sm">
                Nenhuma pessoa encontrada.
              </Text>
            )}
            <Stack gap={4}>
              {filteredPeople.map((p) => (
                <UnstyledButton
                  key={p.user_id}
                  onClick={() =>
                    openDm.mutate(p.user_id, {
                      onSuccess: go,
                      onError: () => notifications.show({ color: 'red', title: 'Erro', message: 'Não foi possível abrir a conversa. Tente novamente.' }),
                    })
                  }
                  px="xs"
                  py={6}
                  style={{ borderRadius: 8 }}
                >
                  <Group gap="sm" wrap="nowrap">
                    <Avatar
                      radius="xl"
                      size="md"
                      color="inovareTeal"
                      variant="filled"
                      src={p.avatar_url ?? undefined}
                      style={{ flexShrink: 0 }}
                    >
                      {getInitials(p.name || p.email)}
                    </Avatar>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {p.name || p.email}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {p.email}
                      </Text>
                    </Box>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </Box>

          {/* Seção: Organizações */}
          <Box>
            <Text fw={600} size="sm" c="dimmed" mb="xs">
              Organizações
            </Text>
            {orgs.isLoading && (
              <Group justify="center" py="sm">
                <Loader size="sm" type="dots" color="teal" />
              </Group>
            )}
            {!orgs.isLoading && filteredOrgs.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="sm">
                Nenhuma organização encontrada.
              </Text>
            )}
            <Accordion variant="separated" chevronPosition="right">
              {filteredOrgs.map((o) => (
                <Accordion.Item key={o.id} value={String(o.id)}>
                  <Accordion.Control>
                    <Group justify="space-between" wrap="nowrap" pr="xs">
                      <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        <Avatar
                          radius="md"
                          size="sm"
                          color="inovareTeal"
                          variant="light"
                          style={{ flexShrink: 0 }}
                        >
                          {getInitials(o.name)}
                        </Avatar>
                        <Text size="sm" fw={500} lineClamp={1}>
                          {o.name}
                        </Text>
                      </Group>
                      <Badge size="sm" variant="light" color="gray" style={{ flexShrink: 0 }}>
                        {o.member_count}
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap={4}>
                      {/* Botão de abrir o grupo da Org */}
                      <UnstyledButton
                        onClick={() => openOrg.mutate(o.id, {
                          onSuccess: go,
                          onError: () => notifications.show({ color: 'red', title: 'Erro', message: 'Não foi possível abrir a conversa. Tente novamente.' }),
                        })}
                        px="xs"
                        py={6}
                        style={{ borderRadius: 6 }}
                      >
                        <Text size="sm" c="inovareTeal" fw={600}>
                          Abrir grupo
                        </Text>
                      </UnstyledButton>

                      {/* Membros listados para abrir DM individual */}
                      {o.members.map((m) => (
                        <UnstyledButton
                          key={m.user_id}
                          onClick={() => openDm.mutate(m.user_id, {
                            onSuccess: go,
                            onError: () => notifications.show({ color: 'red', title: 'Erro', message: 'Não foi possível abrir a conversa. Tente novamente.' }),
                          })}
                          px="xs"
                          py={4}
                          style={{ borderRadius: 6 }}
                        >
                          <Group gap="sm" wrap="nowrap">
                            <Avatar
                              size="sm"
                              radius="xl"
                              color="gray"
                              variant="light"
                              src={m.avatar_url ?? undefined}
                              style={{ flexShrink: 0 }}
                            >
                              {getInitials(m.name || m.email)}
                            </Avatar>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text size="xs" lineClamp={1}>
                                {m.name || m.email}
                                {m.is_owner ? ' (dono)' : ''}
                              </Text>
                              {/* E-mail desambigua homônimos (ex.: dois "Carlos Farah") */}
                              {m.name && (
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {m.email}
                                </Text>
                              )}
                            </Box>
                          </Group>
                        </UnstyledButton>
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Box>
        </Stack>
      </ScrollArea>
    </Drawer>
  )
}
